import { Injectable } from '@angular/core';
import { EnvironmentConfigService } from '../data-services/environment-config.service';
import { LocalStorageService } from '../../xform-compat';
import { environment } from '../../../environments/environment';
import { EnvironmentConfig } from '../../shared/models/environment-config.interface';

/** Which preset/custom source a user picked for the nav color. */
export type EnvColorSource = 'default' | 'navy' | 'custom';

/** A user's persisted nav-color choice for a given environment. */
export interface EnvColorSelection {
  source: EnvColorSource;
  color: string;
}

/** RA Navy — the production nav color, also offered as a "preview prod" option. */
const RA_NAVY = '#102638';
/** localStorage key prefix; the running env name is appended so each env is independent. */
const STORAGE_PREFIX = 'envNavColor';
/** Environments with their own favicon set under assets/images/favicons/; anything else keeps prod's. */
const FAVICON_ENVS: ReadonlyArray<string> = ['dev', 'qa', 'uat'];
/** ids of the index.html favicon links, mapped to the file each one points at. */
const FAVICON_LINKS: Record<string, string> = {
  'favicon': 'favicon.png',
  'favicon-svg': 'favicon.svg',
  'apple-touch-icon': 'apple-touch-icon.png'
};
/** CSS custom property the header SCSS reads. */
const NAV_BG_VAR = '--rms-nav-bg';

/**
 * Owns the lower-env nav-indicator feature: decides whether it is active,
 * persists the per-browser/per-environment color choice, and applies the
 * chosen color to the nav via a CSS custom property on the document root.
 *
 * The feature is INERT unless all hold: the build is not production, the
 * backend reports a non-prod environment, and the config call succeeded.
 * Defaulting to off keeps production untouched.
 *
 * Also swaps the favicon to the backend-reported environment's set; unlike the
 * nav color, that does not depend on the build flag.
 */
@Injectable({
  providedIn: 'root'
})
export class EnvIndicatorService {

  private _isActive = false;
  private _environment = '';
  private _navDefaultColor = '';

  constructor(
    private environmentConfigService: EnvironmentConfigService,
    private localStorageService: LocalStorageService
  ) {}

  public get isActive(): boolean {
    return this._isActive;
  }

  public get environment(): string {
    return this._environment;
  }

  public get navDefaultColor(): string {
    return this._navDefaultColor;
  }

  public get raNavy(): string {
    return RA_NAVY;
  }

  /**
   * Fetches the env config, applies the environment's favicon, then computes the
   * nav-color active state and applies the effective color. Resolves (never
   * rejects) so it is safe to await at bootstrap; any failure leaves both inert.
   *
   * The favicon follows the backend-reported environment regardless of build flag,
   * because the ECS image is a production build shared by every environment (RA-2808).
   * In production builds the favicon is applied in the background so bootstrap never
   * waits on the config call.
   */
  public async init(): Promise<void> {
    const configPromise = this.loadConfig();
    if (environment.production) {
      void this.applyFaviconWhenLoaded(configPromise);
      return;
    }
    const config = await configPromise;
    if (!config?.environment || config.environment === 'prod') {
      return;
    }
    this.applyFavicon(config.environment);
    this._environment = config.environment;
    this._navDefaultColor = config.navDefaultColor || RA_NAVY;
    this._isActive = true;
    this.applyColor(this.getSelection().color);
  }

  /**
   * Returns the stored selection for the current env, or the env default.
   * A stored selection is returned verbatim, so its color is pinned to the
   * value captured when the user chose it (even a 'default' choice keeps that
   * snapshot rather than re-resolving the backend default).
   */
  public getSelection(): EnvColorSelection {
    const stored = this.localStorageService.get(this.storageKey());
    if (stored && stored.color) {
      return stored;
    }
    return { source: 'default', color: this._navDefaultColor };
  }

  /**
   * Persists the chosen color for the current env and applies it immediately.
   */
  public setSelection(source: EnvColorSource, color: string): void {
    const selection: EnvColorSelection = { source, color };
    this.localStorageService.set(this.storageKey(), selection);
    this.applyColor(color);
  }

  /**
   * Returns the env config, or null if the call fails, so callers never have to catch.
   */
  private async loadConfig(): Promise<EnvironmentConfig | null> {
    try {
      return await this.environmentConfigService.getEnvironmentConfig().toPromise();
    } catch {
      return null;
    }
  }

  /**
   * Applies the favicon once the in-flight config call settles. loadConfig never rejects,
   * so awaiting it here keeps the background work free of unhandled promises.
   */
  private async applyFaviconWhenLoaded(configPromise: Promise<EnvironmentConfig | null>): Promise<void> {
    const config = await configPromise;
    this.applyFavicon(config?.environment);
  }

  private storageKey(): string {
    return `${STORAGE_PREFIX}:${this._environment}`;
  }

  private applyColor(color: string): void {
    document.documentElement.style.setProperty(NAV_BG_VAR, color);
  }

  /**
   * Points the index.html favicon links at the environment's own set. The favicon is
   * a fixed environment marker, so it ignores the user's custom nav color.
   */
  private applyFavicon(env?: string): void {
    if (!env || !FAVICON_ENVS.includes(env)) {
      return;
    }
    Object.entries(FAVICON_LINKS).forEach(([id, file]) => {
      const link = document.getElementById(id) as HTMLLinkElement;
      if (link) {
        link.href = `assets/images/favicons/${env}/${file}`;
      }
    });
  }
}
